import type { Workflow } from '../schemas';

// Generate mock workflows for testing
export const mockWorkflows: Workflow[] = (() => {
  const workflowTemplates = [
    {
      name: 'Daily Sales Report',
      description: 'Extract daily sales data for reporting',
      dataSource: 'sales_transactions',
      exportFormat: 'csv' as const,
      filters: [
        { field: 'status', operator: 'equals' as const, value: 'completed' },
        { field: 'amount', operator: 'greaterThan' as const, value: '0' },
      ],
      exportFields: ['transaction_id', 'customer_id', 'amount', 'date', 'product_name'],
    },
    {
      name: 'User Activity Analytics',
      description: 'Track user engagement and activity patterns',
      dataSource: 'user_events',
      exportFormat: 'json' as const,
      filters: [
        { field: 'event_type', operator: 'contains' as const, value: 'click' },
        { field: 'user_id', operator: 'startsWith' as const, value: 'usr_' },
      ],
      exportFields: ['user_id', 'event_type', 'timestamp', 'page_url', 'session_id'],
    },
    {
      name: 'Inventory Management',
      description: 'Monitor stock levels and product availability',
      dataSource: 'inventory_items',
      exportFormat: 'xlsx' as const,
      filters: [
        { field: 'stock_level', operator: 'lessThan' as const, value: '100' },
        { field: 'category', operator: 'equals' as const, value: 'electronics' },
      ],
      exportFields: ['product_id', 'name', 'stock_level', 'category', 'supplier', 'last_updated'],
    },
    {
      name: 'Customer Feedback Analysis',
      description: 'Analyze customer reviews and ratings',
      dataSource: 'customer_reviews',
      exportFormat: 'csv' as const,
      filters: [
        { field: 'rating', operator: 'greaterThan' as const, value: '3' },
        { field: 'verified_purchase', operator: 'equals' as const, value: 'true' },
      ],
      exportFields: ['review_id', 'customer_id', 'rating', 'comment', 'product_id', 'date'],
    },
    {
      name: 'Financial Transactions',
      description: 'Export financial transaction data for accounting',
      dataSource: 'financial_transactions',
      exportFormat: 'xlsx' as const,
      filters: [
        { field: 'type', operator: 'equals' as const, value: 'payment' },
        { field: 'status', operator: 'equals' as const, value: 'processed' },
      ],
      exportFields: [
        'transaction_id',
        'amount',
        'currency',
        'type',
        'status',
        'created_at',
        'account_id',
      ],
    },
    {
      name: 'Marketing Campaign Performance',
      description: 'Track marketing campaign metrics and ROI',
      dataSource: 'campaign_metrics',
      exportFormat: 'json' as const,
      filters: [
        { field: 'campaign_status', operator: 'equals' as const, value: 'active' },
        { field: 'impressions', operator: 'greaterThan' as const, value: '1000' },
      ],
      exportFields: ['campaign_id', 'name', 'impressions', 'clicks', 'conversions', 'spend', 'roi'],
    },
    {
      name: 'Employee Attendance',
      description: 'Monitor employee attendance and working hours',
      dataSource: 'employee_attendance',
      exportFormat: 'csv' as const,
      filters: [
        { field: 'department', operator: 'equals' as const, value: 'engineering' },
        { field: 'hours_worked', operator: 'greaterThan' as const, value: '6' },
      ],
      exportFields: [
        'employee_id',
        'name',
        'department',
        'check_in',
        'check_out',
        'hours_worked',
        'date',
      ],
    },
    {
      name: 'Product Performance Metrics',
      description: 'Analyze product sales and performance data',
      dataSource: 'product_analytics',
      exportFormat: 'xlsx' as const,
      filters: [
        { field: 'sales_count', operator: 'greaterThan' as const, value: '50' },
        { field: 'category', operator: 'contains' as const, value: 'tech' },
      ],
      exportFields: ['product_id', 'name', 'category', 'sales_count', 'revenue', 'profit_margin'],
    },
    {
      name: 'Website Traffic Analysis',
      description: 'Export website traffic and visitor data',
      dataSource: 'web_analytics',
      exportFormat: 'json' as const,
      filters: [
        { field: 'page_views', operator: 'greaterThan' as const, value: '100' },
        { field: 'bounce_rate', operator: 'lessThan' as const, value: '0.5' },
      ],
      exportFields: [
        'page_url',
        'page_views',
        'unique_visitors',
        'bounce_rate',
        'avg_time_on_page',
      ],
    },
    {
      name: 'Support Ticket Analysis',
      description: 'Track customer support tickets and resolution times',
      dataSource: 'support_tickets',
      exportFormat: 'csv' as const,
      filters: [
        { field: 'priority', operator: 'equals' as const, value: 'high' },
        { field: 'status', operator: 'equals' as const, value: 'resolved' },
      ],
      exportFields: [
        'ticket_id',
        'customer_id',
        'priority',
        'category',
        'status',
        'created_at',
        'resolved_at',
      ],
    },
    {
      name: 'Subscription Analytics',
      description: 'Monitor subscription metrics and churn rates',
      dataSource: 'subscriptions',
      exportFormat: 'xlsx' as const,
      filters: [
        { field: 'plan_type', operator: 'equals' as const, value: 'premium' },
        { field: 'status', operator: 'equals' as const, value: 'active' },
      ],
      exportFields: [
        'subscription_id',
        'customer_id',
        'plan_type',
        'status',
        'start_date',
        'next_billing',
      ],
    },
    {
      name: 'Social Media Engagement',
      description: 'Track social media posts and engagement metrics',
      dataSource: 'social_media_posts',
      exportFormat: 'json' as const,
      filters: [
        { field: 'platform', operator: 'equals' as const, value: 'twitter' },
        { field: 'engagement_rate', operator: 'greaterThan' as const, value: '0.05' },
      ],
      exportFields: [
        'post_id',
        'platform',
        'content',
        'likes',
        'shares',
        'comments',
        'engagement_rate',
      ],
    },
    {
      name: 'Email Campaign Results',
      description: 'Analyze email marketing campaign performance',
      dataSource: 'email_campaigns',
      exportFormat: 'csv' as const,
      filters: [
        { field: 'open_rate', operator: 'greaterThan' as const, value: '0.2' },
        { field: 'campaign_type', operator: 'equals' as const, value: 'newsletter' },
      ],
      exportFields: [
        'campaign_id',
        'subject',
        'sent_count',
        'open_rate',
        'click_rate',
        'unsubscribe_rate',
      ],
    },
    {
      name: 'API Usage Statistics',
      description: 'Monitor API endpoint usage and performance',
      dataSource: 'api_logs',
      exportFormat: 'json' as const,
      filters: [
        { field: 'response_time', operator: 'lessThan' as const, value: '500' },
        { field: 'status_code', operator: 'equals' as const, value: '200' },
      ],
      exportFields: ['endpoint', 'method', 'response_time', 'status_code', 'user_id', 'timestamp'],
    },
    {
      name: 'Shipping & Logistics',
      description: 'Track shipping performance and delivery metrics',
      dataSource: 'shipments',
      exportFormat: 'xlsx' as const,
      filters: [
        { field: 'delivery_status', operator: 'equals' as const, value: 'delivered' },
        { field: 'shipping_method', operator: 'contains' as const, value: 'express' },
      ],
      exportFields: [
        'shipment_id',
        'order_id',
        'tracking_number',
        'delivery_status',
        'shipped_date',
        'delivered_date',
      ],
    },
  ];

  const workflows: Workflow[] = [];
  const userIds = ['1', '2']; // Admin and regular user

  workflowTemplates.forEach((template, index) => {
    const userId = userIds[index % userIds.length];
    const createdDate = new Date(
      2024,
      Math.floor(Math.random() * 12),
      Math.floor(Math.random() * 28) + 1,
    );
    const updatedDate = new Date(createdDate.getTime() + Math.random() * 30 * 24 * 60 * 60 * 1000);

    workflows.push({
      id: (index + 1).toString(),
      ...template,
      userId,
      createdAt: createdDate.toISOString(),
      updatedAt: updatedDate.toISOString(),
    });
  });

  return workflows;
})();
